/** Tags the sanitizer keeps. Mirrors the configured admin-panel editor schema. */
export const allowedTags = [
  'a',
  'blockquote',
  'br',
  'code',
  'col',
  'colgroup',
  'del',
  'div',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'li',
  'mark',
  'ol',
  'p',
  'pre',
  's',
  'span',
  'strike',
  'strong',
  'sub',
  'sup',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'u',
  'ul',
] as const;

/**
 * Tags that are known markup and are removed, with their contents where the sanitizer
 * forbids contents. Recognizing them is what keeps a document made only of forbidden
 * markup from being classified as plain text and escaped into visible source.
 */
export const forbiddenTags = [
  'base',
  'button',
  'embed',
  'form',
  'iframe',
  'img',
  'input',
  'link',
  'math',
  'meta',
  'object',
  'picture',
  'script',
  'source',
  'style',
  'svg',
  'track',
  'video',
  'audio',
] as const;

/** Tags whose presence means pasted or submitted content contains executable HTML. */
export const executableTags = ['embed', 'form', 'iframe', 'object', 'script', 'svg'] as const;

/**
 * Forbidden tags whose text content is discarded along with the element.
 *
 * A sanitizer that only unwraps these leaves their chrome behind as prose: the label of a
 * discarded `button` or the caption of a discarded media element would survive into a legal
 * document as loose text. Everything here is interface or executable content, never prose.
 */
export const forbiddenContentTags = [
  'audio',
  'base',
  'button',
  'embed',
  'form',
  'iframe',
  'input',
  'link',
  'math',
  'meta',
  'object',
  'optgroup',
  'option',
  'picture',
  'script',
  'select',
  'source',
  'style',
  'svg',
  'textarea',
  'title',
  'track',
  'video',
] as const;

/**
 * Remaining known native HTML element names. They carry no policy meaning: the sanitizer
 * unwraps them and keeps their text, but their presence still means the value is markup.
 */
export const knownNativeTags = [
  'abbr',
  'acronym',
  'address',
  'applet',
  'area',
  'article',
  'aside',
  'b',
  'basefont',
  'bdi',
  'bdo',
  'big',
  'blink',
  'body',
  'canvas',
  'caption',
  'center',
  'cite',
  'col',
  'colgroup',
  'data',
  'datalist',
  'dd',
  'details',
  'dfn',
  'dialog',
  'dir',
  'dl',
  'dt',
  'fieldset',
  'figcaption',
  'figure',
  'font',
  'footer',
  'frame',
  'frameset',
  'head',
  'header',
  'hgroup',
  'html',
  'i',
  'ins',
  'kbd',
  'label',
  'legend',
  'main',
  'map',
  'marquee',
  'menu',
  'meter',
  'nav',
  'nobr',
  'noscript',
  'optgroup',
  'option',
  'output',
  'param',
  'progress',
  'q',
  'rp',
  'rt',
  'ruby',
  'samp',
  'search',
  'section',
  'select',
  'slot',
  'small',
  'summary',
  'table',
  'tbody',
  'td',
  'template',
  'textarea',
  'tfoot',
  'th',
  'thead',
  'time',
  'title',
  'tr',
  'tt',
  'var',
  'wbr',
] as const;

/**
 * Every tag whose presence makes a value HTML rather than plain text. An unknown or
 * custom element name — `<not-a-tag>` — is deliberately absent, so a legal document that
 * merely mentions one keeps it as visible text.
 */
export const recognizedTags: readonly string[] = Array.from(
  new Set<string>([...allowedTags, ...forbiddenTags, ...knownNativeTags]),
);

/** Attributes allowed on any allowed tag. */
export const globalAllowedAttributes = ['style', 'title'] as const;

/** Attributes allowed only on specific tags. */
export const allowedAttributesByTag: Readonly<Record<string, readonly string[]>> = {
  a: ['href', 'rel', 'target'],
  col: ['span'],
  div: ['data-type'],
  li: ['data-checked', 'data-type'],
  ol: ['data-type'],
  span: ['data-type'],
  td: ['colspan', 'colwidth', 'rowspan'],
  th: ['colspan', 'colwidth', 'rowspan'],
  ul: ['data-type'],
};

/** Flat attribute list for the host's sanitizer pre-pass, which is not tag-aware. */
export const allowedAttributes: readonly string[] = Array.from(
  new Set<string>([
    ...globalAllowedAttributes,
    ...Object.keys(allowedAttributesByTag).reduce<string[]>(
      (names, tag) => names.concat(allowedAttributesByTag[tag] as string[]),
      [],
    ),
  ]),
);

/** The only `text-align` values the configured editor emits. */
export const allowedTextAlignments = ['center', 'justify', 'left', 'right'] as const;

/** Tags that may carry an authored font size. The CSS value is preserved as entered. */
export const fontSizeTags = ['span'] as const;

/** CSS properties whose authored values are preserved without normalization. */
export const preservedStyleProperties = [
  'height',
  'line-height',
  'max-height',
  'max-width',
  'min-height',
  'min-width',
  'width',
] as const;

/** CSS property families whose authored values are preserved without normalization. */
export const preservedStylePropertyPrefixes = ['font', 'margin', 'padding'] as const;

/** Constrained colour form accepted for highlight backgrounds. */
export const safeColorPattern = /^(#[\da-f]{3,8}|rgba?\([\d\s,.%]+\)|hsla?\([\d\s,.%]+\)|[a-z]+)$/i;

/** Tags that may carry a highlight background colour. */
export const backgroundColorTags = ['mark'] as const;

/** Tags that may carry an authored foreground text colour. */
export const textColorTags = ['span'] as const;

/**
 * Characters a URL parser strips from a URL before it parses it. They must be removed here too,
 * or `/<TAB>/host` passes the root-relative arm and still resolves to `//host`.
 */
export const strippedHrefCharacters = /[\t\n\r]/g;

/**
 * Every `href` form the policy allows: absolute web, mail, phone, root-relative, fragment.
 *
 * The negative lookahead after the leading slash is what keeps a root-relative link on-origin.
 * Both `/` and `\` have to be excluded, not just `/`: a URL parser treats `\` as `/` in a special
 * scheme, so `/\host` resolves to `//host` exactly as `//host` does — off-origin, and NOT matched
 * by `externalHrefPattern`, so it would be handed out with no opener isolation and no `noreferrer`.
 * Test against a value that has already been through `normalizeHref`, never the raw attribute.
 */
export const allowedHrefPattern = /^(?:(?:https?|mailto|tel):|\/(?![/\\])|#)/i;

/**
 * A link is external — and therefore opened with opener isolation — when its target is an
 * absolute `http:` or `https:` URL. Deciding this by scheme rather than by comparing the
 * link host against a request origin is what lets the browser host and the two Node hosts
 * reach the same answer for the same stored document.
 */
export const externalHrefPattern = /^https?:/i;

/** Attributes forced onto an external link, overriding whatever the editor emitted. */
export const externalLinkAttributes: Readonly<Record<string, string>> = {
  rel: 'noopener noreferrer',
  target: '_blank',
};

/** Attributes stripped from a link that is not external. */
export const nonExternalLinkAttributes = ['rel', 'target'] as const;

/** Tags that carry no text yet still count as document content. */
export const meaningfulEmptyTags = ['hr'] as const;

/**
 * Limits on the field.
 *
 * `maxRawBytes` guards the transport: it is measured on the raw submitted value in UTF-8
 * bytes, by each service host, before sanitization runs. `maxLength` is the contract limit
 * on the sanitized and normalized value that is actually stored, measured in UTF-16 code
 * units by `RichTextHtmlText.codeUnits`.
 *
 * `class-validator`'s `MaxLength` must not be used for `maxLength`: it delegates to
 * `validator.js` `isLength`, which subtracts surrogate pairs and variation selectors from
 * `str.length`, so an emoji counts as one unit there and two here. A gateway using it would
 * disagree with the admin panel at the boundary.
 */
export const richTextHtmlLimits = {
  maxLength: 200000,
  maxRawBytes: 1000000,
  maxNestingDepth: 256,
} as const;

/** The limits a single Rich text field is bound to. */
export interface IRichTextHtmlLimits {
  maxLength: number;
  maxRawBytes: number;
  maxNestingDepth: number;
}
